import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Tenant {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
}

interface UserState {
  tenants: Tenant[];
  currentTenantId: string | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  setTenants: (tenants: Tenant[]) => void;
  setCurrentTenant: (tenantId: string) => void;
  getCurrentTenant: () => Tenant | null;
  logout: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      tenants: [],
      currentTenantId: null,
      isInitialized: false,
      isLoading: false,
      error: null,

      initialize: async () => {
        set({ isLoading: true, error: null });
        try {
          const { tenants, currentTenantId } = get();

          if (!currentTenantId && tenants.length > 0) {
            set({ currentTenantId: tenants[0]?.id ?? null });
          }

          set({ isInitialized: true });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to initialize' });
        } finally {
          set({ isLoading: false });
        }
      },

      setTenants: (tenants) => set({ tenants }),

      setCurrentTenant: (tenantId) => {
        const { tenants } = get();
        if (tenants.some(t => t.id === tenantId)) {
          set({ currentTenantId: tenantId });
        }
      },

      getCurrentTenant: () => {
        const { tenants, currentTenantId } = get();
        return tenants.find(t => t.id === currentTenantId) ?? null;
      },

      logout: () => set({
        tenants: [],
        currentTenantId: null,
        isInitialized: false,
      }),
    }),
    {
      name: 'zac-user-store',
      partialize: (state) => ({
        tenants: state.tenants,
        currentTenantId: state.currentTenantId,
      }),
    }
  )
);
